import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Copy, Check, Loader2, KeyRound, ArrowRight, ArrowLeft, Lock } from "lucide-react";
import { Card, Badge, SectionLabel } from "../components/ui-bits";
import { useAuth, formatApiError } from "../auth/AuthContext";

/* ---- password strength ---- */
function pwStrength(p) {
  if (!p) return { score: 0, label: "", tone: "" };
  let s = 0;
  if (p.length >= 8) s++;
  if (p.length >= 12) s++;
  if (/[A-Z]/.test(p) && /[a-z]/.test(p)) s++;
  if (/\d/.test(p)) s++;
  if (/[^A-Za-z0-9]/.test(p)) s++;
  const map = [
    { label: "Too weak",   tone: "text-fij-red",    bar: "bg-fij-red",    width: "w-1/5" },
    { label: "Weak",       tone: "text-fij-red",    bar: "bg-fij-red",    width: "w-2/5" },
    { label: "Fair",       tone: "text-fij-amber",  bar: "bg-fij-amber",  width: "w-3/5" },
    { label: "Good",       tone: "text-fij-green",  bar: "bg-fij-green",  width: "w-4/5" },
    { label: "Strong",     tone: "text-fij-green",  bar: "bg-fij-green",  width: "w-full" },
  ];
  return { score: s, ...map[Math.min(s - 1, 4)] || map[0] };
}

function TerminalShell({ children }) {
  return (
    <div className="min-h-screen bg-fij-bg text-fij-text flex flex-col px-4 py-10">
      <div className="flex-1 grid place-items-center">
        <div className="w-full max-w-md space-y-6">
          <div className="text-center space-y-1">
            <div className="inline-flex items-center gap-2 font-mono text-[11px] text-fij-textMuted">
              <span className="w-2 h-2 rounded-full bg-fij-green animate-pulse-green" />
              FIJ TERMINAL · v0.1
            </div>
            <h1 className="fij-mono text-2xl text-fij-text">Financial Impact Journal</h1>
            <div className="pt-1">
              <Link
                to="/dashboard"
                data-testid="auth-back-to-dashboard"
                className="inline-flex items-center gap-1 font-mono text-[11px] text-fij-textDim hover:text-fij-green transition-colors"
              >
                <ArrowLeft size={11} /> Continue browsing as guest
              </Link>
            </div>
          </div>
          {children}
        </div>
      </div>
      <footer
        data-testid="auth-footer"
        className="pt-8 flex flex-wrap items-center justify-center gap-4 font-mono text-[10px] text-fij-textMuted"
      >
        <Link to="/privacy" data-testid="auth-footer-privacy" className="hover:text-fij-green">Privacy Policy</Link>
        <Link to="/terms"   data-testid="auth-footer-terms"   className="hover:text-fij-green">Terms &amp; Conditions</Link>
        <Link to="/contact" data-testid="auth-footer-contact" className="hover:text-fij-green">Contact</Link>
      </footer>
    </div>
  );
}

/* ================== SIGN UP ================== */
export function SignUpPage() {
  const { generateAccessKey, registerFinalize } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState(1); // 1 = form, 2 = key issued, 3 = verify
  const [name, setName]   = useState("");
  const [email, setEmail] = useState("");
  const [pw, setPw]       = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy]   = useState(false);
  const [err, setErr]     = useState("");
  const [accessKey, setAccessKey] = useState("");
  const [enteredKey, setEnteredKey] = useState("");
  const [copied, setCopied] = useState(false);

  const strength = useMemo(() => pwStrength(pw), [pw]);
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const canSubmitForm = name.trim().length >= 2 && emailOk && strength.score >= 3 && !busy;
  const keysMatch = enteredKey.length === 8 && enteredKey === accessKey;

  const handleGetKey = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const key = await generateAccessKey(name.trim(), email.trim().toLowerCase(), pw);
      setAccessKey(key);
      setStep(2);
    } catch (e) { setErr(formatApiError(e)); }
    finally    { setBusy(false); }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!keysMatch) { setErr("Invalid Access Key."); return; }
    setErr(""); setBusy(true);
    try {
      await registerFinalize(email.trim().toLowerCase(), enteredKey);
      navigate("/dashboard", { replace: true });
    } catch (e) { setErr(formatApiError(e)); }
    finally    { setBusy(false); }
  };

  const copyKey = async () => {
    try { await navigator.clipboard.writeText(accessKey); }
    catch { /* fallback */ const ta = document.createElement("textarea"); ta.value = accessKey; document.body.appendChild(ta); ta.select(); document.execCommand("copy"); ta.remove(); }
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  return (
    <TerminalShell>
      <Card data-testid="signup-card" className="space-y-5">
        {step === 1 && (
          <>
            <div>
              <SectionLabel>CREATE_ACCOUNT</SectionLabel>
              <h2 className="fij-mono text-xl text-fij-text mt-1">Create your account</h2>
              <p className="font-mono text-[11px] text-fij-textDim mt-1">Sign up to unlock the FIJ terminal.</p>
            </div>
            <form onSubmit={handleGetKey} className="space-y-4">
              <Field label="Full Name">
                <input
                  data-testid="signup-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoComplete="name"
                  placeholder="Jane Morgan"
                  className={inputCls}
                />
              </Field>
              <Field label="Email Address">
                <input
                  data-testid="signup-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  placeholder="jane@morgan.co"
                  className={inputCls}
                />
              </Field>
              <Field label="Password">
                <div className="relative">
                  <input
                    data-testid="signup-password"
                    type={showPw ? "text" : "password"}
                    value={pw}
                    onChange={(e) => setPw(e.target.value)}
                    autoComplete="new-password"
                    placeholder="At least 8 chars incl. upper, lower, digit"
                    className={inputCls + " pr-10"}
                  />
                  <button
                    type="button"
                    data-testid="signup-password-toggle"
                    onClick={() => setShowPw((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-fij-textMuted hover:text-fij-text"
                    tabIndex={-1}
                  >
                    {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {pw && (
                  <div className="mt-2" data-testid="signup-strength">
                    <div className="h-1 rounded-sm bg-fij-border/60 overflow-hidden">
                      <div className={`h-full ${strength.bar} ${strength.width} transition-all`} />
                    </div>
                    <div className={`mt-1 font-mono text-[10px] ${strength.tone}`}>{strength.label.toUpperCase()}</div>
                  </div>
                )}
              </Field>

              {err && <ErrorLine testId="signup-error" text={err} />}

              <button
                type="submit"
                data-testid="signup-get-key"
                disabled={!canSubmitForm}
                className={btnPrimary(canSubmitForm)}
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />}
                GET ACCESS KEY
              </button>
            </form>

            <div className="text-center pt-2 border-t border-fij-border/60">
              <span className="font-mono text-[11px] text-fij-textDim">Already registered? </span>
              <Link to="/login" data-testid="link-signin" className="font-mono text-[11px] text-fij-green hover:underline">
                Sign In
              </Link>
            </div>
          </>
        )}

        {step === 2 && (
          <div className="space-y-5" data-testid="access-key-issued">
            <div className="flex items-center gap-2">
              <Badge tone="positive"><Check size={10} /> GENERATED</Badge>
              <SectionLabel className="!mt-0">ACCESS_KEY_GENERATED_SUCCESSFULLY</SectionLabel>
            </div>
            <div className="text-center">
              <div className="font-mono text-[11px] text-fij-textDim mb-2">Access Key:</div>
              <div className="flex items-center justify-center gap-2">
                <span
                  data-testid="access-key-value"
                  className="fij-mono text-3xl tracking-widest bg-fij-bg border border-fij-green/40 rounded-sm px-4 py-3 select-all"
                >
                  {accessKey}
                </span>
                <button
                  onClick={copyKey}
                  data-testid="access-key-copy"
                  className="border border-fij-border rounded-sm p-2.5 hover:border-fij-green/40 text-fij-textDim hover:text-fij-green transition-colors"
                  title="Copy"
                >
                  {copied ? <Check size={16} className="text-fij-green" /> : <Copy size={16} />}
                </button>
              </div>
              {copied && (
                <div data-testid="access-key-copied" className="font-mono text-[11px] text-fij-green mt-2">
                  Copied to clipboard
                </div>
              )}
            </div>
            <button
              data-testid="access-key-continue"
              onClick={() => { setStep(3); setErr(""); }}
              className={btnPrimary(true)}
            >
              CONTINUE <ArrowRight size={14} />
            </button>
          </div>
        )}

        {step === 3 && (
          <form className="space-y-4" onSubmit={handleVerify} data-testid="verify-form">
            <div>
              <SectionLabel>VERIFY_ACCESS_KEY</SectionLabel>
              <h2 className="fij-mono text-xl text-fij-text mt-1">Enter Access Key</h2>
            </div>
            <Field label="Access Key">
              <input
                data-testid="verify-key-input"
                value={enteredKey}
                onChange={(e) => { setEnteredKey(e.target.value); setErr(""); }}
                maxLength={8}
                autoComplete="off"
                placeholder="8 characters"
                className={inputCls + " tracking-widest text-center fij-mono text-lg"}
                autoFocus
              />
            </Field>

            {err && <ErrorLine testId="verify-error" text={err} />}
            {!err && enteredKey.length === 8 && enteredKey !== accessKey && (
              <ErrorLine testId="verify-mismatch" text="Invalid Access Key." />
            )}

            <button
              type="submit"
              data-testid="verify-enter-terminal"
              disabled={!keysMatch || busy}
              className={btnPrimary(keysMatch && !busy)}
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Lock size={14} />}
              ENTER TERMINAL
            </button>

            <button
              type="button"
              onClick={() => { setStep(2); setEnteredKey(""); setErr(""); }}
              className="w-full font-mono text-[11px] text-fij-textDim hover:text-fij-text py-1"
            >
              <ArrowLeft size={12} className="inline mr-1" />Show access key again
            </button>
          </form>
        )}
      </Card>
    </TerminalShell>
  );
}

/* ================== SIGN IN ================== */
export function SignInPage() {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [pw, setPw]       = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy]   = useState(false);
  const [err, setErr]     = useState("");

  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const canSubmit = emailOk && pw.length >= 1 && !busy;

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await signIn(email.trim().toLowerCase(), pw);
      navigate("/dashboard", { replace: true });
    } catch (e) { setErr(formatApiError(e)); }
    finally    { setBusy(false); }
  };

  return (
    <TerminalShell>
      <Card data-testid="signin-card" className="space-y-5">
        <div>
          <SectionLabel>SIGN_IN</SectionLabel>
          <h2 className="fij-mono text-xl text-fij-text mt-1">Welcome back</h2>
          <p className="font-mono text-[11px] text-fij-textDim mt-1">Sign in to continue to the terminal.</p>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Email Address">
            <input
              data-testid="signin-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              placeholder="you@company.com"
              className={inputCls}
            />
          </Field>
          <Field label="Password">
            <div className="relative">
              <input
                data-testid="signin-password"
                type={showPw ? "text" : "password"}
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                autoComplete="current-password"
                placeholder="Your password"
                className={inputCls + " pr-10"}
              />
              <button
                type="button"
                data-testid="signin-password-toggle"
                onClick={() => setShowPw((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-fij-textMuted hover:text-fij-text"
                tabIndex={-1}
              >
                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </Field>

          {err && <ErrorLine testId="signin-error" text={err} />}

          <button
            type="submit"
            data-testid="signin-submit"
            disabled={!canSubmit}
            className={btnPrimary(canSubmit)}
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Lock size={14} />}
            SIGN IN
          </button>
        </form>

        <div className="flex items-center justify-between pt-2 border-t border-fij-border/60">
          <button
            type="button"
            onClick={() => window.alert("Password reset — coming soon. Please contact support.")}
            data-testid="link-forgot"
            className="font-mono text-[11px] text-fij-textDim hover:text-fij-text"
          >
            Forgot Password
          </button>
          <Link to="/signup" data-testid="link-signup" className="font-mono text-[11px] text-fij-green hover:underline">
            Back to Sign Up
          </Link>
        </div>
      </Card>
    </TerminalShell>
  );
}

/* ---- helpers ---- */
const inputCls = "w-full bg-fij-bg border border-fij-border rounded-sm px-3 h-10 font-mono text-sm text-fij-text placeholder:text-fij-textMuted outline-none focus:border-fij-green/40 transition-colors";
function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase text-fij-textMuted font-mono mb-1.5">{label}</div>
      {children}
    </label>
  );
}
function btnPrimary(enabled) {
  return `w-full inline-flex items-center justify-center gap-2 font-mono text-xs py-2.5 rounded-sm transition-colors ${
    enabled
      ? "bg-fij-green text-black hover:bg-fij-green/90"
      : "bg-fij-surface border border-fij-border text-fij-textMuted cursor-not-allowed"
  }`;
}
function ErrorLine({ testId, text }) {
  return (
    <div data-testid={testId} className="font-mono text-[11px] text-fij-red border border-fij-red/30 bg-fij-red/5 rounded-sm px-3 py-2">
      {text}
    </div>
  );
}
